"use client"

import { useRef } from 'react'
import { MapPin, Phone, Mail, Clock, Sparkles } from 'lucide-react'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useScrollY } from '@/hooks/use-scroll-progress'
import { useStaggeredAnimation } from '@/hooks/use-animation-delay'

const contactInfo = [
  {
    icon: MapPin,
    title: 'Location',
    details: ['17E/546, Chopasni Housing Board,', 'Jodhpur, Rajasthan — 342008'],
    color: 'from-teal to-teal-dark',
  },
  {
    icon: Phone,
    title: 'Phone',
    details: ['+91 91364 86156'],
    color: 'from-peach to-rose-400',
    link: 'tel:+919136486156',
  },
  {
    icon: Mail,
    title: 'Email',
    details: ['arushisinghaljoshi@gmail.com'],
    color: 'from-amber-400 to-orange-500',
    link: 'mailto:arushisinghaljoshi@gmail.com',
  },
  {
    icon: Clock,
    title: 'Appointments',
    details: ['Mon - Sat: 4:30 PM - 7:30 PM', 'Sun: 11:00 AM - 1:00 PM'],
    color: 'from-indigo-400 to-indigo-600',
  },
]

// Contact info card component
function ContactInfoCard({ 
  info, 
  index, 
  isVisible 
}: { 
  info: typeof contactInfo[0]
  index: number
  isVisible: boolean 
}) {
  const Icon = info.icon
  
  return (
    <div 
      className={`flex items-start gap-4 p-5 rounded-xl bg-white shadow-sm group cursor-pointer relative overflow-hidden card-hover hover:scale-[1.02] hover:translate-x-2 hover:shadow-xl transition-all duration-300 ${isVisible ? 'reveal visible' : 'reveal'}`}
      style={{ transitionDelay: `${index * 50}ms` }}
    >
      {/* Hover gradient */}
      <div className={`absolute inset-0 bg-linear-to-r ${info.color} opacity-0 group-hover:opacity-10 transition-all duration-400 -translate-x-full group-hover:translate-x-0`} />
      
      <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center shrink-0 relative z-10 group-hover:scale-110 transition-all duration-500">
        <Icon className="w-6 h-6 text-teal" />
      </div>
      
      <div className="relative z-10">
        <h4 className="font-semibold text-navy mb-1">{info.title}</h4>
        {info.details.map((detail) =>
          'link' in info && info.link ? (
            <a
              key={detail}
              href={info.link}
              className="text-muted-foreground text-sm hover:text-teal transition-colors underline underline-offset-2"
            >
              {detail}
            </a>
          ) : (
            <p key={detail} className="text-muted-foreground text-sm">{detail}</p>
          )
        )}
      </div>
    </div>
  )
}

export function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const scrollY = useScrollY()
  const { ref: headerRef, isIntersecting: isHeaderVisible } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  const { ref: infoRef, isIntersecting: isInfoVisible } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  
  const backgroundOffset = scrollY * 0.1
  const contactItems = useStaggeredAnimation(4, 100, 50)

  return (
    <section 
      id="contact" 
      ref={sectionRef}
      className="py-16 lg:py-32 bg-linear-to-b from-cream/50 to-background relative overflow-hidden min-h-dvh w-full flex items-center"
    >
      {/* Animated background decoration */}
      <div 
        className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none"
        style={{ transform: `translateY(${backgroundOffset}px)` }}
      >
        <div 
          className="absolute -top-40 -right-40 w-80 h-80 bg-teal/10 rounded-full blur-3xl animate-pulse-soft"
          style={{ animationDuration: '8s' }}
        />
        <div 
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-peach/10 rounded-full blur-3xl animate-pulse-soft"
          style={{ animationDuration: '10s', animationDelay: '2s' }}
        />
      </div>
      
      {/* Floating particles */}
      <div
        className="absolute top-1/4 right-10 w-3 h-3 rounded-full bg-teal/30 hidden sm:block animate-float"
        style={{ animationDuration: '4s' }}
      />
      <div
        className="absolute bottom-1/3 left-20 w-4 h-4 rounded-full bg-peach/30 hidden sm:block animate-float"
        style={{ animationDuration: '5s', animationDelay: '1s' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-12 lg:mb-16 ${isHeaderVisible ? 'reveal visible' : 'reveal'}`}
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6 hover:scale-105 transition-transform duration-300 ${isHeaderVisible ? 'scale-in' : ''}`}>
            <Sparkles className="w-4 h-4" />
            Get in Touch
          </div>
          
          <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 text-balance ${isHeaderVisible ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '100ms' }}
          >
            Book Your{' '}
            <span className="text-gradient">Appointment</span>
          </h2>
          
          <p className={`text-lg text-muted-foreground leading-relaxed ${isHeaderVisible ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '200ms' }}
          >
            Contact Dr. Arushi to schedule a consultation. Based in Jodhpur, Rajasthan.
          </p>
        </div>

        {/* Contact Info - Centered Layout */}
        <div ref={infoRef} className="max-w-4xl mx-auto">
          <h3 className={`font-serif text-2xl font-bold text-navy mb-8 text-center ${isInfoVisible ? 'reveal visible' : 'reveal'}`}>
            Contact Information
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {contactInfo.map((info, index) => (
              <ContactInfoCard 
                key={info.title}
                info={info}
                index={index}
                isVisible={isInfoVisible}
              />
            ))}
          </div>

          {/* Google Maps Embed */}
          <div 
            className={`rounded-2xl overflow-hidden shadow-lg relative hover:scale-[1.01] transition-transform duration-300 ${isInfoVisible ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '100ms' }}
          >
            <div className="relative w-full aspect-video">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3577.976534108627!2d72.96402057575541!3d26.262424987733176!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418fe551dcbc4d%3A0xc1dd0bd0277ef8e5!2sDr%20Arushi%20Singhal%20Joshi!5e0!3m2!1sen!2sin!4v1769237737147!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="absolute inset-0 w-full h-full"
                title="Dr. Arushi Singhal Joshi - Jodhpur, Rajasthan"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
